import Avatar from 'components/Avatar';
import { DollarCircle, InfoIcon, Task } from 'components/Icons';
import InlineMessage from 'components/InlineMessage';
import ConfirmationDialogue from 'components/modals/ConfirmationDialogue';
import styles from 'components/projects/ProjectDeleteConfirmationDialogue.module.scss';
import TypeToConfirm from 'components/TypeToConfirm';
import React, { useState } from 'react';
import FontAwesome from 'react-fontawesome';

export type UserActionDetails = { id: number; name?: string; src?: string } | null;

interface Props {
  userActionDetails: UserActionDetails;
  setUserActionDetails: (userActionDetails: UserActionDetails) => void;
  handleRemoveUser: () => void;
}

const UserRemoveConfirmationDialogue: React.FC<Props> = ({
  userActionDetails,
  setUserActionDetails,
  handleRemoveUser,
}) => {
  const [hasTypedToConfirm, setHasTypedToConfirm] = useState(false);

  if (!userActionDetails) {
    return null;
  }

  return (
    <ConfirmationDialogue
      title={`Remove ${userActionDetails.name}`}
      buttons={[
        {
          text: "Cancel, don't remove",
          priority: 'high',
          onClick: () => setUserActionDetails(null),
        },
      ]}
      isOpen={userActionDetails != null}
      onClose={() => setUserActionDetails(null)}
      width={600}
    >
      <div className={styles.warningContent}>
        <InlineMessage type={hasTypedToConfirm ? 'alert' : 'neutral'}>
          <ul className={styles.consequencesList}>
            <li>
              <span className={styles.projectBadgeWrap}>
                {userActionDetails.src && (
                  <Avatar
                    shape="circle"
                    src={userActionDetails.src}
                    name={userActionDetails.name}
                  />
                )}
              </span>
              <p style={{ lineHeight: 1.3 }}>
                <strong>{userActionDetails.name}</strong> will be permanently removed from this
                workspace, but their data will remain visible. They can be re-added by inviting them
                to the workspace with the same email address
              </p>
            </li>
            <li>
              <span className={styles.iconWrap}>
                <FontAwesome style={{ marginRight: 5, color: '#697077' }} name="clock" />
              </span>
              <p>
                <strong>All entries</strong> logged by this user will continue to be available
              </p>
            </li>
            <li>
              <span className={styles.iconWrap}>
                <Task fill="#697077" />
              </span>
              <p>
                <strong>All tasks</strong> assigned to this user will be disassociated
              </p>
            </li>
            <li>
              <span className={styles.iconWrap}>
                <DollarCircle fill="#697077" color="white" />
              </span>
              <p>
                <strong>All invoices</strong> based on this project will continue to be available
              </p>
            </li>
            <li>
              <span className={styles.iconWrap}>
                <InfoIcon backgroundColor="#697077" color="white" />
              </span>
              <p>
                <strong>All reports</strong> that include this user will stay the same
              </p>
            </li>
          </ul>
          <div className={styles.confirmWrap}>
            <p>Type REMOVE to confirm</p>
            <TypeToConfirm
              confirmString="REMOVE"
              onConfirm={handleRemoveUser}
              onChange={setHasTypedToConfirm}
              confirmButtonProps={{
                text: 'Permanently remove user',
                icon: <FontAwesome style={{ marginRight: 5 }} name="trash" />,
                priority: 'high',
                destructive: true,
              }}
            />
          </div>
        </InlineMessage>
      </div>
    </ConfirmationDialogue>
  );
};

export default UserRemoveConfirmationDialogue;
